import { useEffect, useLayoutEffect, useState } from 'react';

const svgCache: Record<string, string> = {};
const pendingRequests: Record<string, Promise<string> | undefined> = {};

export const useDynamicSVGImport = (name: string) => {
  // First paint: hydrate from cache; uncached = loading so Icon doesn’t render null (flicker).
  const [svgContent, setSvgContent] = useState<string | null>(() => svgCache[name] ?? null);
  const [loading, setLoading] = useState(() => !svgCache[name]);
  const [error, setError] = useState<any>();

  // Before paint when `name` changes—clears stale SVG / wrong icon flash.
  useLayoutEffect(() => {
    const cached = svgCache[name];
    if (cached) {
      setSvgContent(cached);
      setLoading(false);
      setError(undefined);
    } else {
      setSvgContent(null);
      setLoading(true);
      setError(undefined);
    }
  }, [name]);

  // Fetch or await shared pending request (layout already set loading for uncached).
  useEffect(() => {
    let isMounted = true;

    const fetchSvg = async () => {
      if (svgCache[name]) {
        // If the SVG is already cached, return it
        setSvgContent(svgCache[name]);
        setLoading(false);
        return;
      }

      const pendingRequest = pendingRequests[name];
      if (pendingRequest) {
        try {
          const svgText = await pendingRequest;
          if (isMounted) {
            setSvgContent(svgText);
            setLoading(false);
          }
        } catch (err) {
          if (isMounted) {
            console.error(`Error fetching SVG ${name}:`, err);
            setError(err);
            setLoading(false);
          }
        }
        return;
      }

      const fetchRequest = (async () => {
        try {
          const response = await fetch(`/icons/${name}.svg`);
          if (!response.ok) {
            throw new Error(`Failed to fetch SVG: ${response.status} ${response.statusText}`);
          }
          const svgText = await response.text();
          svgCache[name] = svgText; // Cache the SVG content locally
          return svgText;
        } catch (err) {
          throw err;
        } finally {
          delete pendingRequests[name]; // Clean up pending request once resolved
        }
      })();
      pendingRequests[name] = fetchRequest;

      try {
        const svgText = await fetchRequest;
        if (isMounted) {
          setSvgContent(svgText);
        }
      } catch (err) {
        if (isMounted) {
          console.error(`Error fetching SVG ${name}:`, err);
          setError(err);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }

    };

    void fetchSvg();

    return () => {
      isMounted = false;
    };
  }, [name]);

  return {
    error,
    loading,
    svgContent,
  };
};